import { useCallback, useContext } from "react";

import { AuthContext } from "@/contexts/AuthContext";
import { BYOC_PARENT_TILE_ID } from "@/features/Connectors/byocConstants";

/** Minimal shape needed to spot the BYOC tile inside any dragon tiles list. */
export type ByocTileLike = {
  name?: string;
  type?: string;
  slug?: string;
};

/**
 * The gateway marks the tile with `type=byoc`; some payloads only carry the
 * slug, so accept either.
 */
export function isByocDragonTile(tile: ByocTileLike): boolean {
  return tile.type === BYOC_PARENT_TILE_ID || tile.slug === BYOC_PARENT_TILE_ID;
}

/**
 * "BYOC" is an internal name that should never reach the UI, so the tile is
 * labelled with the site name instead (FOG-2400) — the same substitution the
 * Dragon Tile grid and the BYOC workspace header already make. The API name is
 * only a fallback for when the site is missing from the session.
 */
export function resolveDragonTileDisplayName(
  tile: ByocTileLike,
  siteName: string,
): string {
  const apiName = tile.name ?? "";
  if (!isByocDragonTile(tile)) return apiName;
  return siteName.trim() || apiName;
}

/**
 * The site's display name from the session, or "" when it isn't known.
 *
 * Reads the auth context directly rather than through `useAuth` so the
 * presentational components below it — several of which render outside a
 * provider in tests and stories — degrade to the API name instead of throwing.
 */
export function useSiteName(siteId: string): string {
  const auth = useContext(AuthContext);
  const sites = auth?.sites ?? [];
  return sites.find((site) => site.id === siteId)?.name?.trim() ?? "";
}

/**
 * Resolver for surfaces that render names straight off a dragon tiles list
 * (`type=all` and the unfiltered list both include the BYOC tile).
 */
export function useDragonTileDisplayName(
  siteId: string,
): (tile: ByocTileLike) => string {
  const siteName = useSiteName(siteId);

  return useCallback(
    (tile: ByocTileLike) => resolveDragonTileDisplayName(tile, siteName),
    [siteName],
  );
}

/**
 * Display label for the surfaces that name a dragon tile to the user (frame
 * card chips, editor headers, breadcrumbs). Capitalization only tidies up the
 * API's tile names; a site name stands in for BYOC and is shown exactly as the
 * site spells it.
 */
export function resolveDragonTileLabel(
  tile: ByocTileLike,
  siteName: string,
): string {
  const name = resolveDragonTileDisplayName(tile, siteName);
  return isByocDragonTile(tile)
    ? name
    : name.charAt(0).toUpperCase() + name.slice(1);
}

/** Resolver for the surfaces that label dragon tiles off a dragon tiles list. */
export function useDragonTileLabel(
  siteId: string,
): (tile: ByocTileLike) => string {
  const siteName = useSiteName(siteId);

  return useCallback(
    (tile: ByocTileLike) => resolveDragonTileLabel(tile, siteName),
    [siteName],
  );
}
